import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Camera, RotateCcw, UserCheck, ArrowLeft, CheckCircle2, XCircle } from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { staffAPI, API_BASE_URL } from '../../services/api';

export default function RegisterFace() {
  const navigate = useNavigate();
  const { staffId } = useParams();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const [staffList, setStaffList] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(staffId || '');
  const [capturedImage, setCapturedImage] = useState(null);
  const [capturedBlob, setCapturedBlob] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    staffAPI.list({ status: 'ACTIVE' }).then((res) => {
      setStaffList(res.data || []);
    }).catch(() => {});
  }, []);

  const startCamera = useCallback(async () => {
    setCameraError('');
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 960 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setCameraActive(true);
    } catch {
      setCameraError('Camera access denied.');
      setCameraActive(false);
    }
  }, []);

  useEffect(() => {
    startCamera();
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
    };
  }, [startCamera]);

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    setCapturedImage(canvas.toDataURL('image/jpeg', 0.95));
    canvas.toBlob((blob) => setCapturedBlob(blob), 'image/jpeg', 0.95);
  };

  const retake = () => {
    setCapturedImage(null);
    setCapturedBlob(null);
    setResult(null);
    startCamera();
  };

  const submitPhoto = async () => {
    if (!selectedStaff || !capturedBlob) return;
    setSubmitting(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('photo', capturedBlob, 'face.jpg');
      formData.append('staff_id', selectedStaff);

      const token = localStorage.getItem('auth_token');
      const facilityId = localStorage.getItem('active_facility_id');

      const response = await fetch(`${API_BASE_URL}/selfie-attendance/register-face`, {
        method: 'POST',
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
          ...(facilityId && { 'X-Facility-Id': facilityId }),
        },
        body: formData,
      });
      const data = await response.json();
      setResult(data);

      if (data.success && streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
    } catch (err) {
      setResult({ success: false, message: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-lg mx-auto space-y-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-sm text-ga-text-muted hover:text-ga-text-primary transition"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>

        <div>
          <h1 className="text-2xl font-bold text-ga-text-primary flex items-center gap-2">
            <UserCheck className="h-7 w-7 text-teal-600" />
            Register Face
          </h1>
          <p className="text-ga-text-muted mt-1">Capture a clear face photo for attendance verification</p>
        </div>

        <div className="ga-card rounded-xl shadow-sm border p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-ga-text-primary mb-1">Staff Member</label>
            <select
              value={selectedStaff}
              onChange={(e) => setSelectedStaff(e.target.value)}
              className="ga-input w-full px-3 py-2 rounded-lg"
              disabled={!!staffId}
            >
              <option value="">-- Select staff member --</option>
              {staffList.map((s) => (
                <option key={s.id} value={s.id}>{s.name} ({s.role})</option>
              ))}
            </select>
          </div>

          {result?.success ? (
            <div className="text-center py-8 space-y-4">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100">
                <CheckCircle2 className="h-9 w-9 text-emerald-600" />
              </div>
              <div>
                <p className="text-lg font-semibold text-ga-text-primary">Face Registered</p>
                <p className="text-sm text-ga-text-muted mt-1">{result.data?.name || 'Staff'} is now enrolled for selfie attendance.</p>
              </div>
              <button
                onClick={() => navigate('/dashboard/selfie-attendance')}
                className="px-6 py-2 rounded-lg bg-teal-600 text-white font-medium hover:bg-teal-700 transition"
              >
                Go to Selfie Attendance
              </button>
            </div>
          ) : (
            <>
              {!capturedImage ? (
                <div>
                  <div className="relative rounded-xl overflow-hidden bg-gray-900 aspect-[3/4]">
                    {cameraError ? (
                      <div className="absolute inset-0 flex items-center justify-center p-6 text-center text-white">
                        <div>
                          <Camera className="h-12 w-12 mx-auto mb-3 opacity-50" />
                          <p className="text-sm">{cameraError}</p>
                          <button onClick={startCamera} className="mt-3 text-teal-400 underline text-sm">Retry</button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <video
                          ref={videoRef} autoPlay playsInline muted
                          className="w-full h-full object-cover"
                          style={{ transform: 'scaleX(-1)' }}
                        />
                        <div className="absolute inset-0 pointer-events-none">
                          <svg className="w-full h-full" viewBox="0 0 300 400" preserveAspectRatio="xMidYMid slice">
                            <defs>
                              <mask id="reg-mask">
                                <rect width="300" height="400" fill="white" />
                                <ellipse cx="150" cy="170" rx="75" ry="100" fill="black" />
                              </mask>
                            </defs>
                            <rect width="300" height="400" fill="rgba(0,0,0,0.45)" mask="url(#reg-mask)" />
                            <ellipse cx="150" cy="170" rx="75" ry="100" fill="none" stroke="#14b8a6" strokeWidth="2.5" strokeDasharray="8 4" />
                          </svg>
                        </div>
                      </>
                    )}
                  </div>
                  <canvas ref={canvasRef} className="hidden" />
                  <div className="flex justify-center mt-4">
                    <button
                      onClick={capturePhoto}
                      disabled={!cameraActive || !selectedStaff}
                      className="px-8 py-3 rounded-full bg-teal-600 text-white font-semibold
                                 hover:bg-teal-700 disabled:opacity-40 transition shadow-lg
                                 flex items-center gap-2"
                    >
                      <Camera className="h-5 w-5" /> Capture
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="rounded-xl overflow-hidden border-2 border-teal-200">
                    <img src={capturedImage} alt="Face" className="w-full aspect-[3/4] object-cover" style={{ transform: 'scaleX(-1)' }} />
                  </div>
                  {result && !result.success && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                      {result.message || 'Registration failed. Please try again.'}
                    </div>
                  )}
                  <div className="flex gap-3">
                    <button onClick={retake} disabled={submitting}
                      className="flex-1 px-4 py-3 rounded-xl border border-gray-300 text-ga-text-primary font-medium hover:bg-gray-50 disabled:opacity-40 transition flex items-center justify-center gap-2">
                      <RotateCcw className="h-4 w-4" /> Retake
                    </button>
                    <button onClick={submitPhoto} disabled={submitting}
                      className="flex-1 px-4 py-3 rounded-xl bg-teal-600 text-white font-semibold hover:bg-teal-700 disabled:opacity-40 transition shadow flex items-center justify-center gap-2">
                      {submitting ? (
                        <>
                          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                          </svg>
                          Processing...
                        </>
                      ) : (
                        <>
                          <UserCheck className="h-4 w-4" /> Register
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
